/* Collection, collections list and search pages: sidebar blocks, recently viewed and load-more pagination
   (collection-search-cart.md §5.2 and §6). */

if (!customElements.get('collection-sidebar')) {
  const mobile = window.matchMedia('(max-width: 798px)');

  class CollectionSidebar extends HTMLElement {
    connectedCallback() {
      this.allowToggle = this.dataset.sidebarAllowToggle === 'true';
      this.onBreakpoint = () => this.applyBreakpoint();
      mobile.addEventListener('change', this.onBreakpoint);
      this.applyBreakpoint();
      this.addEventListener('click', this.onClick);
      this.loadRecentlyViewed();
    }

    disconnectedCallback() {
      mobile.removeEventListener('change', this.onBreakpoint);
    }

    blocks() {
      return this.querySelectorAll('details[data-sidebar-block]');
    }

    // Phones: every block closes on load and on entering the breakpoint. Desktop without `toggle`: always open.
    applyBreakpoint() {
      if (mobile.matches) this.blocks().forEach((block) => (block.open = false));
      else if (!this.allowToggle) this.blocks().forEach((block) => (block.open = true));
    }

    onClick = (event) => {
      const summary = event.target.closest('summary');
      if (summary?.parentElement.matches('[data-sidebar-block]') && !mobile.matches && !this.allowToggle) {
        event.preventDefault();
        return;
      }

      const toggle = event.target.closest('.menu-toggle');
      const parentLink = event.target.closest('.parent-link--false');
      const button = toggle || parentLink?.parentElement.querySelector(':scope > .menu-toggle');
      if (!button) return;
      event.preventDefault();
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', expanded);
      button.nextElementSibling.hidden = !expanded;
    };

    async loadRecentlyViewed() {
      const box = this.querySelector('.js-sidebar-recently-viewed');
      if (!box || box.dataset.recentlyViewedItemsLoaded) return;
      box.dataset.recentlyViewedItemsLoaded = 'true';
      const cookie = document.cookie.split('; ').find((row) => row.startsWith('recentlyViewed='));
      if (!cookie) return;
      const handles = decodeURIComponent(cookie.slice('recentlyViewed='.length))
        .split(',')
        .map((handle) => decodeURIComponent(handle).trim())
        .filter((handle) => handle && handle !== box.dataset.rvHandle)
        .slice(0, Number(box.dataset.visibleProducts) || 3);

      const slots = box.querySelectorAll('.rv-element');
      await Promise.all(
        handles.map(async (handle, index) => {
          try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?section_id=recently-viewed-card`);
            if (!response.ok) return;
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = html.querySelector('[data-rv-card]') || html.querySelector('.shopify-section')?.firstElementChild;
            if (!card || !slots[index]) return;
            const wrapper = document.createElement('div');
            wrapper.className = 'column thumbnail js-recently-viewed-product';
            wrapper.append(card);
            slots[index].replaceChildren(wrapper);
          } catch (error) {
            console.error(error);
          }
        })
      );
    }
  }

  customElements.define('collection-sidebar', CollectionSidebar);
}

if (!customElements.get('load-more')) {
  /* load_more_button: each click appends the next page. load_more: the first click appends and switches on
     infinite scroll. infinite_scroll: pages append as the grid end nears. The URL never changes. */
  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-load-more]');
      this.grid = document.getElementById(this.dataset.grid);
      if (!this.link || !this.grid) return;
      if (this.dataset.mode === 'infinite_scroll') this.observe();
      else
        this.link.addEventListener('click', (event) => {
          event.preventDefault();
          this.load().then(() => {
            if (this.dataset.mode === 'load_more' && this.link) {
              this.link.classList.add('is-hidden');
              this.observe();
            }
          });
        });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    observe() {
      this.observer ||= new IntersectionObserver((entries) => entries.some((entry) => entry.isIntersecting) && this.load(), {
        rootMargin: '0px 0px 400px 0px',
      });
      this.observer.observe(this);
    }

    async load() {
      if (this.loading || !this.link) return;
      this.loading = true;
      const url = this.link.href;
      this.classList.add('loading-in-progress', 'is-loading');
      this.link.classList.add('is-hidden');
      try {
        const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${this.dataset.sectionId}`);
        if (!response.ok) throw new Error(response.status);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = html.getElementById(this.dataset.grid)?.children;
        if (!items) throw new Error('Grid missing from next page');
        this.grid.append(...items);
        const next = html.querySelector('load-more [data-load-more]');
        if (next) {
          this.link.href = next.href;
          if (this.dataset.mode === 'load_more_button') this.link.classList.remove('is-hidden');
        } else {
          this.link.remove();
          this.link = null;
          this.observer?.disconnect();
          this.hidden = true;
        }
      } catch (error) {
        // A failed fetch still moves the shopper on (decisions.md #4).
        window.location.href = url;
        return;
      } finally {
        this.loading = false;
        this.classList.remove('loading-in-progress', 'is-loading');
      }
      // Re-arm the observer so a short page keeps loading while the end is still in view.
      if (this.observer && this.link) {
        this.observer.unobserve(this);
        this.observer.observe(this);
      }
    }
  }

  customElements.define('load-more', LoadMore);
}
